import { useEffect, useRef, useState, type JSX } from 'react'
import {
  AlertTriangle,
  AlignLeft,
  Bot,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  Edit3,
  Network,
  Pencil,
  RotateCcw,
  Shield,
  Variable,
  X } from 'lucide-react'
import RequirePermission from '../../auth/RequirePermission.js'

interface PromptVersion {
  version: number
  content: string
  variables: string[]
  updatedBy: string | null
  createdAt: string
  notes?: string
}

interface PromptLinkedAgent {
  id: string
  slug: string
  name: string
  type: string
}

interface PromptTemplate {
  id: string
  slug: string
  name: string
  description: string
  category: 'orchestrator' | 'agent' | 'routing' | 'summary' | 'safety'
  currentVersion: number
  content: string
  variables: string[]
  updatedAt: string
  versions: PromptVersion[]
  linkedAgents?: PromptLinkedAgent[]
}

interface PromptDetailDrawerProps {
  prompt: PromptTemplate | null
  isOpen: boolean
  onClose: () => void
  onEdit: () => void
  onRollback: (version: number) => void
}

const CATEGORY_META: Record<
  PromptTemplate['category'],
  { label: string; icon: JSX.Element; badge: string; bg: string; text: string }
> = {
  agent: {
    label: 'Agent',
    icon: <Bot className="h-3.5 w-3.5" />,
    badge: 'bg-blue-100 text-blue-800 border-blue-200',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
  },
  orchestrator: {
    label: 'Orchestrator',
    icon: <Network className="h-3.5 w-3.5" />,
    badge: 'bg-orange-100 text-orange-800 border-orange-200',
    bg: 'bg-orange-50',
    text: 'text-orange-700',
  },
  routing: {
    label: 'Routing',
    icon: <Network className="h-3.5 w-3.5" />,
    badge: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
  },
  summary: {
    label: 'Summary',
    icon: <AlignLeft className="h-3.5 w-3.5" />,
    badge: 'bg-purple-100 text-purple-800 border-purple-200',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
  },
  safety: {
    label: 'Safety',
    icon: <Shield className="h-3.5 w-3.5" />,
    badge: 'bg-red-100 text-red-800 border-red-200',
    bg: 'bg-red-50',
    text: 'text-red-700',
  },
}

function SectionCard({
  title,
  children,
  action,
  className = '',
}: {
  title?: string
  children: React.ReactNode
  action?: JSX.Element
  className?: string
}): JSX.Element {
  return (
    <div className={`rounded-xl border border-gray-200 bg-white p-4 shadow-sm ${className}`}>
      {(title || action) && (
        <div className="mb-3 flex items-center justify-between">
          {title && <h3 className="text-sm font-semibold text-gray-900">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </div>
  )
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

function extractVariables(content: string): string[] {
  const matches = content.match(/\{\{([a-zA-Z0-9_]+)\}\}/g)
  if (!matches) return []
  return Array.from(new Set(matches.map((m) => m.replace(/[{}]/g, ''))))
}

function useFocusTrap(
  ref: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
  onEscape: () => void,
) {
  useEffect(() => {
    if (!isOpen) return
    const drawer = ref.current
    if (!drawer) return

    const focusables = () =>
      Array.from(
        drawer.querySelectorAll<HTMLElement>(
          'input, textarea, button, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled'))

    const first = focusables()[0]
    first?.focus()

    function handleKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        onEscape()
        return
      }
      if (e.key !== 'Tab' || !drawer) return
      const list = focusables()
      if (list.length === 0) return
      const firstEl = list[0]!
      const lastEl = list[list.length - 1]!
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault()
        lastEl.focus()
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault()
        firstEl.focus()
      }
    }

    document.addEventListener('keydown', handleKeydown)
    return () => document.removeEventListener('keydown', handleKeydown)
  }, [isOpen, onEscape, ref])
}

export default function PromptDetailDrawer({
  prompt,
  isOpen,
  onClose,
  onEdit,
  onRollback,
}: PromptDetailDrawerProps): JSX.Element | null {
  const drawerRef = useRef<HTMLDivElement>(null)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)

  useEffect(() => {
    if (isOpen) setIsHistoryOpen(false)
  }, [isOpen, prompt?.id])

  useFocusTrap(drawerRef, isOpen, onClose)

  if (!isOpen || !prompt) return null

  const meta = CATEGORY_META[prompt.category]
  const variables = extractVariables(prompt.content)

  function copyToClipboard(text: string): void {
    void navigator.clipboard.writeText(text).then(() => {})
  }

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="prompt-detail-title">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" onClick={onClose} aria-hidden="true" />

      <div
        ref={drawerRef}
        className="absolute right-0 top-0 flex h-full w-full flex-col bg-white shadow-2xl md:w-[480px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 p-4">
          <div className="flex min-w-0 items-center gap-2">
            <Pencil className="h-4 w-4 shrink-0 text-gray-500" />
            <span id="prompt-detail-title" className="min-w-0 truncate text-sm font-semibold text-gray-900">
              {prompt.name}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <RequirePermission permission="admin:extensions:manage" fallback={null}>
              <button
                type="button"
                onClick={onEdit}
                className="inline-flex items-center gap-1.5 rounded-sm border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
              >
                <Edit3 className="h-3.5 w-3.5" /> Edit
              </button>
            </RequirePermission>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close drawer"
              className="rounded-sm p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-4 overflow-y-auto p-4 pb-6">
          {/* Header info */}
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium ${meta.badge}`}>
              {meta.icon}
              {meta.label}
            </span>
            <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] font-medium text-gray-600">
              v{prompt.currentVersion}
            </span>
          </div>

          <SectionCard>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-gray-700">Slug:</span>
                <span className="font-mono text-gray-600">{prompt.slug}</span>
              </div>
              <div className="hidden h-3 w-px bg-gray-200 sm:block" />
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-gray-700">Updated:</span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDate(prompt.updatedAt)}
                </span>
              </div>
            </div>
          </SectionCard>

          {/* Description */}
          <SectionCard title="Description">
            <p className="text-sm text-gray-700">
              {prompt.description || <span className="text-gray-400">No description provided.</span>}
            </p>
          </SectionCard>

          {/* Prompt template */}
          <SectionCard
            title="Prompt Template"
            action={
              <button
                type="button"
                onClick={() => copyToClipboard(prompt.content)}
                className="inline-flex items-center gap-1 text-[10px] font-medium text-[#3454cf] hover:underline"
              >
                <Copy className="h-3 w-3" /> Copy
              </button>
            }
          >
            <div className="max-h-80 overflow-y-auto rounded-sm border border-gray-100 bg-gray-50 p-3">
              <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-gray-800">
                {prompt.content}
              </pre>
            </div>

            {variables.length > 0 && (
              <div className="mt-3 flex items-start gap-2 rounded-sm border border-blue-200 bg-blue-50/60 p-2.5">
                <Variable className="mt-0.5 h-4 w-4 shrink-0 text-[#3454cf]" />
                <div className="flex flex-wrap gap-1.5">
                  {variables.map((v) => (
                    <span key={v} className="rounded-sm bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-800">
                      {'{{' + v + '}}'}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </SectionCard>

          {/* Linked agents warning */}
          {prompt.linkedAgents && prompt.linkedAgents.length > 0 && (
            <SectionCard className="border-amber-200 bg-amber-50/30" title="Linked Agents">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <div className="space-y-2">
                  <p className="text-xs font-medium text-amber-800">
                    Editing this prompt will affect {prompt.linkedAgents.length} linked agent{prompt.linkedAgents.length === 1 ? '' : 's'}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {prompt.linkedAgents.map((agent) => (
                      <span
                        key={agent.id}
                        className="inline-flex items-center gap-1 rounded border border-amber-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-amber-900"
                      >
                        <Bot className="h-3 w-3" />
                        {agent.name} <span className="text-amber-600/80">({agent.type})</span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </SectionCard>
          )}

          {/* Version history */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <button
              type="button"
              onClick={() => setIsHistoryOpen((prev) => !prev)}
              className="flex w-full items-center justify-between text-left"
            >
              <h3 className="text-sm font-semibold text-gray-900">Version history ({prompt.versions.length})</h3>
              {isHistoryOpen ? (
                <ChevronUp className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              )}
            </button>

            {isHistoryOpen && (
              <div
                className={`mt-3 flex flex-col gap-3 ${prompt.versions.length > 5 ? 'max-h-[24rem] overflow-y-auto pr-1 thin-scrollbar' : ''}`}
              >
                {prompt.versions.map((v) => {
                  const isCurrent = v.version === prompt.currentVersion
                  return (
                    <div
                      key={v.version}
                      className={`rounded-xl border p-3 text-xs ${
                        isCurrent ? 'border-emerald-200 bg-emerald-50/30' : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-900">Version v{v.version}</span>
                            {isCurrent && (
                              <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800">
                                Active
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex items-center gap-1.5 text-[11px] text-gray-500">
                            <Clock className="h-3 w-3" />
                            {formatDate(v.createdAt)}
                          </div>
                          {v.notes && <p className="mt-1 text-gray-600">{v.notes}</p>}
                        </div>
                        {!isCurrent && (
                          <RequirePermission permission="admin:extensions:manage" fallback={null}>
                            <button
                              onClick={() => onRollback(v.version)}
                              className="flex items-center gap-1 rounded-sm border border-gray-200 bg-white px-2 py-1 text-[10px] font-medium text-gray-700 transition-colors hover:bg-gray-100"
                            >
                              <RotateCcw className="h-3 w-3" /> Rollback
                            </button>
                          </RequirePermission>
                        )}
                      </div>

                      {!isCurrent && (
                        <div className="mt-3 rounded-sm border border-gray-100 bg-gray-50 p-2.5">
                          <pre className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-gray-700">
                            {v.content}
                          </pre>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
